import React, { useState, useEffect } from 'react';
import ErrorIcon from '@/components/icons/ErrorIcon';
import WarningIcon from '@/components/icons/WarningIcon';
import InfoIcon from '@/components/icons/InfoIcon';
import { AlertMode } from './types';
import styles from './Alert.module.css';


interface AlertProps {
  generalMessage: string;
  detailMessage: string;
  isVisible: boolean;
  onClose: () => void;
  mode?: AlertMode;
  duration?: number;
}

const classes = (classesArray: string[]) => {
  return classesArray.join(" ");
}

const Alert: React.FC<AlertProps> = ({ generalMessage, detailMessage, isVisible, onClose, mode = AlertMode.ERROR, duration = 6000 }) => {
  const [shouldRender, setShouldRender] = useState(isVisible);

  const cardClass = (mode: AlertMode) => {
    switch (mode) {
      case AlertMode.WARNING:
        return styles.warningCard;
      case AlertMode.INFO:
        return styles.infoCard;
      default:
        return styles.errorCard;
    }
  }

  if (isVisible && !shouldRender) {
    setShouldRender(true);
  }

  useEffect(() => {
    if (isVisible && duration && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const handleAnimationEnd = () => {
    if (!isVisible) {
      setShouldRender(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={classes([styles.alertCard, `${cardClass(mode)}`, `${isVisible ? styles.slideAlertDown : styles.slideAlertUp}`])}
      onAnimationEnd={handleAnimationEnd}
      role="alert"
      aria-live="assertive"
    >
      <div className={styles.closeRow}>
        <div className={styles.closeButton} onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 11 11" fill="#currentColor">
            <path d="M0 1.69434L3.50879 5.20312L0 8.71191L1.69434 10.4062L5.20312 6.89746L8.71191 10.4062L10.4062 8.71191L6.89746 5.20312L10.4062 1.69434L8.71191 0L5.20312 3.50879L1.69434 0L0 1.69434Z" fill="#575757"/>
          </svg>
        </div>
      </div>
      <div className={styles.cardContent}>
        <span className={styles.errorMessage}>{generalMessage}</span>
        <div className={styles.details}>
          <div className={styles.detailsIcon}>
            {mode === AlertMode.ERROR && <ErrorIcon fill="#FFFFFF" /> }
            {mode === AlertMode.WARNING && <WarningIcon fill="#575757" /> }
            {mode === AlertMode.INFO && <InfoIcon fill="#FFFFFF" /> }
          </div>
          <div className={styles.detailsMessage}>
            {detailMessage}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alert;